import React, { useEffect, useState } from 'react'
import { useToast } from '../../store/modules/ui'
import { useCssHandles } from '../hooks/useCssHandles'

const CSS_HANDLES = [
  'toastContainer',
  'toast',
  'toastContent',
  'toastIcon',
  'toastText',
  'toastTitle',
  'toastMessage',
  'toastClose',
  'toastProgress'
] as const

const Toast: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { state, removeToast } = useToast()
  const [progress, setProgress] = useState<{ [key: string]: number }>({})

  useEffect(() => {
    state.toasts.forEach(toast => {
      if (!progress[toast.id]) {
        setProgress(prev => ({ ...prev, [toast.id]: 100 }))
        
        const interval = setInterval(() => {
          setProgress(prev => {
            const current = prev[toast.id] || 100
            const newProgress = current - (100 / (toast.duration || 4000) * 16) // 60fps
            
            if (newProgress <= 0) {
              clearInterval(interval)
              return { ...prev, [toast.id]: 0 }
            }
            
            return { ...prev, [toast.id]: newProgress }
          })
        }, 16)
      }
    })
  }, [state.toasts, progress])

  const getToastStyles = (type: string) => {
    switch (type) {
      case 'success':
        return {
          border: 'border-green-500',
          icon: 'text-green-600',
          iconBg: 'bg-green-50',
          iconSvg: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )
        }
      case 'error':
        return {
          border: 'border-red-500',
          icon: 'text-red-600',
          iconBg: 'bg-red-50',
          iconSvg: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )
        }
      case 'warning':
        return {
          border: 'border-yellow-500',
          icon: 'text-yellow-600',
          iconBg: 'bg-yellow-50',
          iconSvg: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )
        }
      case 'info':
        return {
          border: 'border-blue-500',
          icon: 'text-blue-600',
          iconBg: 'bg-blue-50',
          iconSvg: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          )
        }
      default:
        return {
          border: 'border-gray-500',
          icon: 'text-gray-600',
          iconBg: 'bg-gray-50',
          iconSvg: (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          )
        }
    }
  }

  if (state.toasts.length === 0) return null

  return (
    <div className={`${handles.toastContainer} fixed bottom-24 right-6 z-50 space-y-4`}>
      {state.toasts.map((toast) => {
        const styles = getToastStyles(toast.type)
        const currentProgress = progress[toast.id] || 100

        return (
          <div
            key={toast.id}
            className={`${handles.toast} bg-white border-l-4 ${styles.border} rounded-2xl shadow-2xl p-5 min-w-96 max-w-md transform transition-all duration-500 ease-out animate-in slide-in-from-right-full backdrop-blur-sm`}
            style={{
              borderLeftColor: styles.icon.replace('text-', '').replace('-600', '-500'),
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
            }}
          >
            <div className={`${handles.toastContent} flex items-start space-x-4`}>
              {/* Ícone com animação */}
              <div className={`${handles.toastIcon} ${styles.iconBg} ${styles.icon} rounded-full p-3 flex-shrink-0 transform transition-all duration-300 hover:scale-110`}>
                {styles.iconSvg}
              </div>

              {/* Conteúdo */}
              <div className={`${handles.toastText} flex-1 min-w-0`}>
                <h4 className={`${handles.toastTitle} text-base font-bold text-gray-900 mb-1`}>
                  {toast.title}
                </h4>
                {toast.message && (
                  <p className={`${handles.toastMessage} text-sm text-gray-600 leading-relaxed`}>
                    {toast.message}
                  </p>
                )}
              </div>

              {/* Botão de fechar com hover effect */}
              <button
                onClick={() => removeToast(toast.id)}
                className={`${handles.toastClose} text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 flex-shrink-0 transition-all duration-200`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Barra de progresso moderna */}
            <div className={`${handles.toastProgress} mt-4 h-1 bg-gray-100 rounded-full overflow-hidden`}>
              <div
                className="h-full rounded-full transition-all duration-100 ease-linear"
                style={{
                  width: `${currentProgress}%`,
                  background: `linear-gradient(90deg, ${styles.icon.replace('text-', '').replace('-600', '-500')}, ${styles.icon.replace('text-', '').replace('-600', '-400')})`
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default Toast

